// Types matching the actual sudhan `document_extraction_services` contract.
//
// We use the backend's snake_case field names directly so the UI doesn't
// have to know about a mapping layer. Some optional fields (e.g.
// classification, byte_size) come from a future enriched contract — they
// are typed but not populated today.

// ----------------------------------------------------------------- documents

/**
 * Stage / status string returned by the backend in the `status` field of a
 * tracking row, e.g. `LINEITEM_EXTRACTION_COMPLETED`. Treated as an opaque
 * string so we never have to keep an enum in sync with backend changes.
 * Convenience helpers below format and color these for the UI.
 */
export type RawStatus = string;

export interface VerificationStatusMap {
  // Codes vary: TV (Tools View), BC (Business Check), BR (Business Rule), …
  // Values are color signals (green | yellow | red).
  [code: string]: "green" | "yellow" | "red" | string;
}

/** A row from POST /document_extraction/tracking_page — `status[]`. */
export interface DocumentRecord {
  id: number;
  execution_id: string;
  process_id: string;
  customer_name: string;
  service: string;
  layout_id: string;
  workflow_id: string;
  /** Raw "DD/MM/YYYY HH:mm:ss" string from the API. */
  start_time: string;
  duration: string;
  uploader: string;
  /** Backend serializes this as a JSON string. We parse it before rendering. */
  verification_status: string | null;
  status: RawStatus;
  filename: string;
}

/** Convenience: a row + parsed verification status. UI works with this. */
export interface DocumentRow extends DocumentRecord {
  verification: VerificationStatusMap;
  /** Best-effort ISO 8601 derived from `start_time` ("DD/MM/YYYY HH:mm:ss"). */
  startedAtIso: string | null;
}

// ----------------------------------------------------------------- socket events

/**
 * Action verb the OCR ws-server attaches to every frame.
 *
 *   "insert"           — a new row appeared in `id_details`.
 *   "update"           — `id_details` carries the changed columns for an
 *                        existing row keyed by `process_id`.
 *   "process_complete" — an async pipeline step (field_item / line_item
 *                        extraction) finished; cockpit consumers refresh
 *                        the projection on this.
 *
 * Treated as an open string union so unknown actions don't crash the
 * parser — handlers simply ignore them.
 */
export type OcrTrackingAction =
  | "insert"
  | "update"
  | "process_complete"
  | string;

/**
 * Raw frame from the OCR ws-server (native WebSocket at
 * wss://eroyal.pmdgtech.com/ws). The reference UI keys off these exact
 * field names — see ocr-ui/src/shared/tracking/TrackingManager.jsx and
 * ocr-ui/src/modules/document-extraction/tools/ToolsView.jsx for the
 * authoritative consumers.
 */
export interface OcrTrackingFrame {
  /** Which backend table the frame is about. May be omitted on some updates. */
  table_name?: string;
  action: OcrTrackingAction;
  /** Stable document id. Either this or `execution_id` is present. */
  process_id?: string;
  /** Reconciliation tracking uses execution_id instead of process_id. */
  execution_id?: string;
  /** Set on field_item / line_item completion frames. */
  type?: "field_item" | "line_item" | string;
  /** Optional human-readable detail (often used as a toast body). */
  message?: string | null;
  /**
   * Row payload — either the full new row (insert) or the changed columns
   * (update). The `user_id` sub-field is the sender's id, used by the UI
   * to drop frames meant for other users.
   */
  id_details?: Partial<DocumentRecord> & {
    user_id?: string;
    service?: string;
    layout_id?: string;
    [key: string]: unknown;
  };
}

/**
 * Loosely-typed stage event derived from an OcrTrackingFrame for the
 * "live queue" UI. One frame produces one stage event when both
 * `process_id` and `status` are present in `id_details`.
 */
export interface ExtractionStageEvent {
  /** Either `process_id` or a generic `documentId` is acceptable. */
  process_id?: string;
  documentId?: string;
  /** Stage / status string the backend emitted. */
  status: RawStatus;
  /** Optional ISO timestamp; falls back to "now" when absent. */
  at?: string;
  /** Optional human-readable detail. */
  message?: string | null;
}

/** Resolve a stable id from a stage event regardless of which field the backend used. */
export function eventDocumentId(evt: ExtractionStageEvent): string {
  return evt.process_id || evt.documentId || "";
}

/** Resolve a stable id from a frame regardless of which field the backend used. */
export function frameDocumentId(frame: OcrTrackingFrame): string {
  return (
    frame.process_id ||
    frame.execution_id ||
    frame.id_details?.process_id ||
    frame.id_details?.execution_id ||
    ""
  );
}

// ----------------------------------------------------------------- requests

export interface TrackingPageRequest {
  pageNumber?: number;     // backend: page_number, default 1
  recordsCount?: number;   // backend: records_count, default 10
  appName?: string;        // backend: app_name, e.g. "po-invoice-extraction"
  /** Free-text search across filename / process_id / customer / layout. */
  search?: string;
  /** Filter by document status (e.g. "TOOLS_VIEW_COMPLETED"). */
  status?: string;
  /** Filter by customer name. */
  customer?: string;
  /** Filter by layout id. */
  layoutId?: string;
  /** Filter by uploader. */
  uploader?: string;
  /** Inclusive date range as ISO date strings (YYYY-MM-DD). */
  dateFrom?: string;
  dateTo?: string;
}

/** Coerced shape of the tracking_search_options response. */
export interface TrackingSearchOptions {
  customers: string[];
  layouts: string[];
  uploaders: string[];
  statuses: string[];
  workflows: string[];
}

export interface TrackingPageResponse {
  rows: DocumentRow[];
  totalRecords: number;
}

export interface ManualUploadRequest {
  layoutId?: string;
  fileName: string;
  fileBase64: string;
  uploader: string;
  /**
   * App namespace the backend uses to scope the upload (required by the
   * `manualUploadData` pydantic model on `/document_extraction/manual_upload`).
   * Defaults to `DEFAULT_APP_NAME` inside the API client when omitted.
   */
  appName?: string;
}

export interface ManualUploadResponse {
  status?: "success" | "error" | string;
  message?: string;
  error?: string | null;
  documentId?: string;
  process_id?: string;
}

export interface DashboardInformationResponse {
  total?: number;
  in_progress?: number;
  completed?: number;
  failed?: number;
  byStatus?: Record<string, number>;
  // Anything else the backend returns — passes through.
  [key: string]: unknown;
}

// ----------------------------------------------------------------- layouts

/**
 * A row from POST /document_extraction/management_layout/layout_id_list.
 *
 * The exact backend shape isn't pinned down yet, so optional fields are
 * defensive — only `layout_id` is required for the UI to function. Anything
 * extra the backend returns flows through via the index signature.
 */
export interface LayoutSummary {
  layout_id: string;
  customer_name?: string | null;
  workflow_id?: string | null;
  document_extraction_type?: string | null; // e.g. "llm" | "rule"
  input_type?: string | null; // e.g. "import" | "export"
  field_count?: number | null;
  lineitem_count?: number | null;
  updated_at?: string | null;
  /** Future-proofing: anything unknown is accessible. */
  [key: string]: unknown;
}

export interface LayoutListResponse {
  layouts: LayoutSummary[];
  totalRecords: number;
}

// ----------------------------------------------------------------- attributes

/**
 * A row from one of:
 *   POST /document_extraction/lineitem_attribute_list
 *   POST /document_extraction/fielditem_attribute_list
 *
 * Mirrors the reference's `mock-data.json` shape exactly — the backend
 * returns `{status: "success", data: [AttributeRecord[]]}` (see
 * ocr-ui/.../config-studio/field-identifier/tabs/LineItemTab.jsx).
 */
export interface AttributeRecord {
  id: string | number;
  identifier: string;
  /** Data type label, e.g. "Text" | "Number" | "Decimal" | "Date" | "Custom". */
  type: string;
  /** Regex pattern derived from `type`, or custom when type === "Custom". */
  pattern: string;
  description: string;
  /** "Active" | "Inactive". */
  status: string;
}

export type AttributeSection = "line_item" | "field_item";

/**
 * Response of POST /document_extraction/management_layout/layout_id_select
 * (and a tolerant general "loose object" type used by the verification
 * cockpit's JSON-tree fallback). Schema varies across endpoints; only
 * `[key: string]: unknown` is guaranteed.
 */
export interface LayoutDetail {
  layout_id?: string;
  customer_name?: string | null;
  document_extraction_type?: string | null;
  input_type?: string | null;
  fielditem?: unknown;
  lineitem?: unknown;
  llm?: unknown;
  workflow_id?: string | null;
  [key: string]: unknown;
}

// ----------------------------------------------------------------- verification

/**
 * Response of POST /document_extraction/verification_home — loose typing so
 * the cockpit renders whatever shape the backend returns. The fields below
 * are the ones the UI tries to read; anything else passes through.
 */
export interface VerificationData {
  process_id?: string;
  filename?: string;
  layout_id?: string;
  customer_name?: string;
  uploader?: string;
  workflow_id?: string;
  status?: string;
  /** Document image URL or base64 if backend embeds it. */
  image_url?: string | null;
  image_base64?: string | null;
  /** Fielditem extraction result (header-style fields). */
  fielditem?: unknown;
  /** Line-item extraction result (tabular). */
  lineitem?: unknown;
  /** OCR text per page, when available. */
  pages?: unknown;
  /** Verification signal map (TV / BC / BR). */
  verification_status?: string | VerificationStatusMap | null;
  [key: string]: unknown;
}

// ----------------------------------------------------------------- helpers

/**
 * Parse a sudhan "DD/MM/YYYY HH:mm:ss" timestamp into ISO 8601.
 * Returns null when the format doesn't match.
 */
export function parseSudhanDate(s: string | null | undefined): string | null {
  if (!s) return null;
  const m = s.match(
    /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/,
  );
  if (!m) return null;
  const [, dd, mm, yyyy, hh, mi, ss] = m;
  // Treat as local time. If the backend ever switches to UTC we'll add `Z`.
  const iso = `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Coerce the backend's `verification_status` into a VerificationStatusMap.
 *
 * The REST API serializes this field as a JSON-encoded string, but the
 * WebSocket frame fan-out emits it as an already-parsed object. Both
 * shapes must be handled or live row updates wipe the verification dots:
 *
 *   - string  → JSON.parse, expect an object back
 *   - object  → use as-is (drops null / array values)
 *   - else    → return empty map (missing / malformed → no badges)
 */
export function parseVerificationStatus(
  raw: unknown,
): VerificationStatusMap {
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as VerificationStatusMap)
        : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return raw as VerificationStatusMap;
  }
  return {};
}

/**
 * Pretty-print a backend status string like "LINEITEM_EXTRACTION_COMPLETED"
 * → "Lineitem extraction completed".
 */
export function formatStatusLabel(s: RawStatus | null | undefined): string {
  if (!s) return "—";
  const lower = s.toLowerCase().replace(/_/g, " ");
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export type StatusTone = "neutral" | "running" | "ok" | "warn" | "fail";

/**
 * Heuristic tone for a backend status string. We don't have a canonical
 * list, so we infer from suffix and a few well-known fragments.
 */
export function statusTone(s: RawStatus | null | undefined): StatusTone {
  if (!s) return "neutral";
  const upper = s.toUpperCase();
  if (upper.endsWith("_FAILED") || upper.includes("ERROR")) return "fail";
  if (upper.endsWith("_REJECTED")) return "warn";
  if (upper.includes("VERIFICATION") && upper.endsWith("_COMPLETED")) return "ok";
  if (upper.includes("OUTPUT") && upper.endsWith("_COMPLETED")) return "ok";
  if (upper.endsWith("_COMPLETED")) return "running";
  if (upper.endsWith("_STARTED") || upper.endsWith("_IN_PROGRESS")) return "running";
  return "neutral";
}

/**
 * Combine raw status with the verification map into a single tone.
 * Verification takes precedence when it carries a definitive signal,
 * because the user cares more about "is this doc done?" than "what
 * pipeline stage is it in?". Used by the inbox row status chip and
 * the preset (All / In progress / Completed / Failed) counters.
 *
 *   - any review code (TV / BC) is red  → fail
 *   - any review code is warn (yellow/amber/orange) → warn
 *   - every review code green            → ok ("submitted / verified")
 *   - otherwise                          → fall through to statusTone(status)
 *
 * RC (Reconciliation) is intentionally excluded — it's an independent
 * batch step that fires separately from the per-doc TV/BC review cycle.
 */
const REVIEW_CODES = ["TV", "BC"] as const;
const RED_SIGNALS = new Set(["red", "rose"]);
const GREEN_SIGNALS = new Set(["green", "emerald"]);
const WARN_SIGNALS = new Set(["yellow", "amber", "orange"]);

export function combinedStatusTone(
  status: RawStatus | null | undefined,
  verification: VerificationStatusMap | undefined,
): StatusTone {
  if (verification) {
    const present = REVIEW_CODES.filter((c) => verification[c] != null);
    if (present.length > 0) {
      const signals = present.map((c) =>
        String(verification[c] ?? "").toLowerCase(),
      );
      if (signals.some((s) => RED_SIGNALS.has(s))) return "fail";
      if (signals.some((s) => WARN_SIGNALS.has(s))) return "warn";
      if (signals.every((s) => GREEN_SIGNALS.has(s))) return "ok";
    }
  }
  return statusTone(status);
}
/**
 * Document types supported by the classification engine.
 */
export type DocumentClassification =
  | "invoice"
  | "purchase_order"
  | "contract"
  | "receipt"
  | "shipping_note"
  | "unknown";
